/**
 * VRAM a geometry could give back — the geometry counterpart to texture
 * compression savings.
 *
 * THE TRAP THIS EXISTS TO EXPOSE: Draco and meshopt shrink the DOWNLOAD and do
 * nothing for GPU memory. Draco decompresses back to full-precision floats, so
 * "I Draco'd it and my VRAM didn't move" is the expected outcome, not a mistake.
 * What actually shrinks VRAM is QUANTIZATION (KHR_mesh_quantization, which
 * meshopt normally ships alongside) and narrower indices — which is what this
 * measures.
 *
 * Only what can be computed EXACTLY from the live buffers is counted. How much
 * indexing would save depends on how weldable a mesh is, and guessing that would
 * be fake precision, so a non-indexed mesh is flagged rather than priced.
 */

/** What quantization would store each attribute as. The standard glTF targets. */
const QUANTIZED_BYTES: Record<string, { bytes: number, to: string }> = {
    position: { bytes: 2, to: "int16" },
    normal: { bytes: 1, to: "int8" },
    tangent: { bytes: 1, to: "int8" },
    uv: { bytes: 2, to: "uint16" },
    uv1: { bytes: 2, to: "uint16" },
    uv2: { bytes: 2, to: "uint16" },
    uv3: { bytes: 2, to: "uint16" },
    color: { bytes: 1, to: "uint8" },
    skinWeight: { bytes: 1, to: "uint8" },
};

/** A uint16 index can address this many vertices. */
const UINT16_VERTEX_LIMIT = 65_536;

export interface AttributeSaving {
    readonly name: string;
    /** what it is stored as now */
    readonly from: string;
    /** what quantization would store it as */
    readonly to: string;
    readonly bytes: number;
}

export interface GeometryVramSaving {
    /** bytes saved by quantizing float attributes */
    readonly quantize: number;
    /** bytes saved by narrowing 32-bit indices the mesh does not need */
    readonly indexWidth: number;
    /** quantize + indexWidth. Only exactly-computable bytes are in here. */
    readonly total: number;
    /** no index buffer: indexing usually cuts vertex storage a lot, but by how
     *  much depends on the mesh, so it is reported as a fact, not a number */
    readonly nonIndexed: boolean;
    readonly attributes: readonly AttributeSaving[];
}

interface AttributeLike { count?: number; itemSize?: number; array?: ArrayBufferView & { BYTES_PER_ELEMENT?: number } }
interface GeometryLike { attributes?: Record<string, AttributeLike | undefined>; index?: { count?: number; array?: ArrayBufferView & { BYTES_PER_ELEMENT?: number } } | null }

const EMPTY: GeometryVramSaving = { quantize: 0, indexWidth: 0, total: 0, nonIndexed: false, attributes: [] };

/** Name of the typed array a buffer uses, for the "from" label. */
function arrayKind(array: ArrayBufferView | undefined): string {
    const name = array?.constructor?.name ?? "";
    return name.replace("Array", "").replace("Float32", "float32").replace("Float", "float").toLowerCase();
}

/**
 * @param geometry a BufferGeometry (typed loosely — this reads host-page objects)
 */
export function analyzeGeometryVram(geometry: GeometryLike | null | undefined): GeometryVramSaving {
    const attributes = geometry?.attributes;
    if (!attributes || typeof attributes !== "object") return EMPTY;

    const savings: AttributeSaving[] = [];
    let quantize = 0;
    let vertexCount = 0;

    for (const name of Object.keys(attributes)) {
        const attribute = attributes[name];
        const array = attribute?.array;
        const perElement = array?.BYTES_PER_ELEMENT;
        const count = attribute?.count ?? 0;
        const itemSize = attribute?.itemSize ?? 0;
        if (!array || !perElement || count <= 0 || itemSize <= 0) continue;

        if (name === "position") vertexCount = count;

        const target = QUANTIZED_BYTES[name];
        // an attribute we have no rule for is left alone rather than guessed at
        if (!target || perElement <= target.bytes) continue;

        const bytes = count * itemSize * (perElement - target.bytes);
        if (bytes <= 0) continue;
        quantize += bytes;
        savings.push({ name, from: arrayKind(array), to: target.to, bytes });
    }

    // A 32-bit index on a mesh a 16-bit one could address is pure waste — exact,
    // unlike everything else here that depends on encoder choices.
    const index = geometry?.index;
    let indexWidth = 0;
    if (index?.array && (index.array.BYTES_PER_ELEMENT ?? 0) > 2 && vertexCount > 0 && vertexCount < UINT16_VERTEX_LIMIT) {
        indexWidth = (index.count ?? 0) * ((index.array.BYTES_PER_ELEMENT ?? 4) - 2);
    }

    return {
        quantize,
        indexWidth,
        total: quantize + indexWidth,
        nonIndexed: !index,
        attributes: savings,
    };
}
